import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Briefcase, ExternalLink, User, X as XIcon, Sparkles } from "lucide-react";
import { WikiBacklinks } from "./WikiBacklinks";

/** [[EntityName]] cross-reference chip + slide-in panel.
 *
 *  When markdown text contains `[[Sarah Amir]]`, the Markdown
 *  component (with `entityRefs` prop) replaces it with an
 *  <EntityRef name="Sarah Amir" /> chip. Tapping the chip resolves
 *  the name to an entity (person, engagement, topic) via
 *  /api/entities/resolve and opens a right-edge slide-in panel
 *  with the entity's detail. The user can then tap "Open" to
 *  navigate to the canonical detail surface, or close the panel
 *  to stay in context.
 *
 *  The panel is a portal-rendered <dialog>-like overlay. Closes on
 *  Escape, on backdrop click, and on the X button. Keeps the user
 *  in their current context (Today, Brief, Recall reply, etc.). */

type EntityKind = "person" | "engagement" | "topic" | "none";

interface ResolveResponse {
  kind: EntityKind;
  id: string | null;
  display_name: string;
  detail_url: string | null;
  candidates?: Array<{
    kind: Exclude<EntityKind, "none">;
    id: string;
    display_name: string;
    detail_url: string;
  }>;
}

interface PersonDetail {
  person: {
    id: string;
    display_name: string;
    primary_email: string | null;
    role: string | null;
    organization: string | null;
    notes: string | null;
    last_interacted_at: string | null;
  };
  interactions?: Array<{ id: string; kind: string; summary: string | null; occurred_at: string }>;
  relationships?: Array<{ type: string }>;
}

interface EngagementDetail {
  engagement: {
    id: string;
    kind: string;
    title: string;
    description: string | null;
    status: string;
    due_at: string | null;
    activity_count: number;
  };
}

export function EntityRef({ name }: { name: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 text-orange-800 dark:text-orange-200 text-[0.85em] font-semibold px-1.5 py-0 mx-0.5 align-baseline transition-colors"
        title={`Open ${name}`}
      >
        {name}
      </button>
      {open && <EntityPanel name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

function EntityPanel({ name, onClose }: { name: string; onClose: () => void }) {
  const [resolved, setResolved] = useState<ResolveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<PersonDetail | EngagementDetail | null>(null);

  useEffect(() => {
    fetch(`/api/entities/resolve?name=${encodeURIComponent(name)}`, { credentials: "same-origin" })
      .then((r) => r.json() as Promise<ResolveResponse>)
      .then((d) => setResolved(d))
      .catch((e: Error) => setError(e.message));
  }, [name]);

  useEffect(() => {
    if (!resolved || !resolved.id || resolved.kind === "none" || resolved.kind === "topic") return;
    const url =
      resolved.kind === "person"
        ? `/api/people/${encodeURIComponent(resolved.id)}`
        : `/api/engagements/${encodeURIComponent(resolved.id)}`;
    fetch(url, { credentials: "same-origin" })
      .then((r) => r.json() as Promise<PersonDetail | EngagementDetail>)
      .then((d) => setDetail(d))
      .catch((e: Error) => setError(e.message));
  }, [resolved]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-md bg-white dark:bg-slate-900 sm:border-l border-slate-200 dark:border-slate-700 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 backdrop-blur">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{name}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </header>

        {error && (
          <div className="px-4 sm:px-6 py-3 text-sm text-rose-600 dark:text-rose-300">{error}</div>
        )}

        {!resolved ? (
          <Skeleton />
        ) : resolved.kind === "none" ? (
          <NoMatch name={name} />
        ) : (
          <Body resolved={resolved} detail={detail} onOpen={(url) => { window.location.assign(url); }} />
        )}

        {resolved && resolved.kind !== "none" && (
          <WikiBacklinks
            entityType={resolved.id ? resolved.kind : undefined}
            entityId={resolved.id ?? undefined}
            name={name}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

function Skeleton() {
  return (
    <div className="px-4 sm:px-6 py-6 space-y-3">
      <div className="h-12 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
      <div className="h-32 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
    </div>
  );
}

function NoMatch({ name }: { name: string }) {
  return (
    <div className="px-4 sm:px-6 py-6 text-center">
      <Sparkles className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-500" strokeWidth={2.5} />
      <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-slate-100">
        No match for "{name}"
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
        Memso doesn't recognise this name yet. It may surface once the next pipeline pass extracts it from a capture.
      </p>
    </div>
  );
}

function Body({
  resolved,
  detail,
  onOpen,
}: {
  resolved: ResolveResponse;
  detail: PersonDetail | EngagementDetail | null;
  onOpen: (url: string) => void;
}) {
  if (resolved.kind === "person") {
    const personDetail = detail as PersonDetail | null;
    const p = personDetail?.person;
    return (
      <div className="px-4 sm:px-6 py-5 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white shadow-md flex-shrink-0 text-base font-bold">
            <User className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 break-words">
              {resolved.display_name}
            </h3>
            {p && (p.role || p.organization) && (
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                {[p.role, p.organization].filter(Boolean).join(" · ")}
              </div>
            )}
            {p?.primary_email && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {p.primary_email}
              </div>
            )}
          </div>
        </div>
        {personDetail?.interactions && personDetail.interactions.length > 0 && (
          <section>
            <h4 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
              Recent interactions
            </h4>
            <ul className="space-y-1.5">
              {personDetail.interactions.slice(0, 5).map((i) => (
                <li key={i.id} className="text-xs text-slate-700 dark:text-slate-200 truncate">
                  <span className="text-slate-500 dark:text-slate-400 mr-2">
                    {new Date(i.occurred_at).toLocaleDateString()}
                  </span>
                  {i.summary || i.kind}
                </li>
              ))}
            </ul>
          </section>
        )}
        {resolved.detail_url && (
          <button
            type="button"
            onClick={() => onOpen(resolved.detail_url!)}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2"
          >
            Open profile
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }
  if (resolved.kind === "engagement") {
    const engagementDetail = detail as EngagementDetail | null;
    const e = engagementDetail?.engagement;
    return (
      <div className="px-4 sm:px-6 py-5 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
            <Briefcase className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              {(e?.kind || "engagement").replace(/_/g, " ")}
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 break-words">
              {resolved.display_name}
            </h3>
            {e?.due_at && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Due {new Date(e.due_at).toLocaleString()}
              </div>
            )}
            {e?.description && (
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap line-clamp-4">
                {e.description}
              </p>
            )}
          </div>
        </div>
        {resolved.detail_url && (
          <button
            type="button"
            onClick={() => onOpen(resolved.detail_url!)}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2"
          >
            Open engagement
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }
  // topic
  return (
    <div className="px-4 sm:px-6 py-5 space-y-4">
      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
        {resolved.display_name}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Topic. Search Memso for everything that mentions it.
      </p>
      {resolved.detail_url && (
        <button
          type="button"
          onClick={() => onOpen(resolved.detail_url!)}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2"
        >
          Search topic
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
